/**
 * The Pick'em Game tab (ISSUE-1304, ISSUE-1309), mounted by
 * `features/poolScreen/poolTypePanes.tsx`. Top to bottom: the round strip,
 * the round's progress line, every game on the slate with its own lock, the
 * confidence order in a confidence pool, the tiebreaker guess when the pool
 * uses one, and one Save picks button. One column at every width.
 *
 * Other members' picks are not here. They show on the Results tab, game by
 * game, once each game locks.
 */
import { Button, InlineBanner, SavedNote, Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { useOverlay } from "../../routeState/useOverlay";
import {
  NEW_CONTEST_DIALOG_NAME,
  NEW_CONTEST_START_ACTION_LABEL,
} from "../newContest/newContestModel";
import { SpreadConsentDialog } from "../pick/SpreadConsentDialog";
import { formatKickoffLabel } from "../pick/pickDeadlineModel";
import type { typePoolTypePaneProps } from "../poolScreen/poolTypePaneProps";
import {
  PICKEM_ALL_PICKS_IN,
  PICKEM_CANNOT_PICK_NOTES,
  PICKEM_EMPTY_STATE_NOTES,
  PICKEM_GAME_FAILED,
  PICKEM_GAME_LOADING,
  PICKEM_GAMES_HEADING,
  PICKEM_NO_GAMES,
  PICKEM_PICKS_FULL_NOTE,
  PICKEM_RETRY,
  PICKEM_SAVE_LABEL,
  PICKEM_SAVED_LABEL,
  PICKEM_SAVING_LABEL,
  pickemMyScoreLine,
  pickemNextLockLine,
  pickemProgressLine,
  pickemStillNeededLine,
} from "./pickemCopy";
import { pickemGameLabel } from "./pickemFormat";
import { PickemConfidenceOrder } from "./PickemConfidenceOrder";
import { PickemGameCard } from "./PickemGameCard";
import {
  canOfferPickemNewContest,
  PICKEM_NEXT_CONTEST_LINE,
} from "./pickemNewContestModel";
import { PickemRoundStrip } from "./PickemRoundStrip";
import { PickemTiebreakerField } from "./PickemTiebreakerField";
import { usePickemGameTab } from "./usePickemGameTab";

const LoadingCards = ({ testId }: { testId: string }): ReactNode => (
  <div className="flex flex-col gap-3" data-testid={testId}>
    <Skeleton shape="card" label={PICKEM_GAME_LOADING} />
    <Skeleton shape="card" label={PICKEM_GAME_LOADING} />
    <Skeleton shape="card" label={PICKEM_GAME_LOADING} />
  </div>
);

export const PickemGameTab = ({
  poolId,
  viewerPoolRole,
}: typePoolTypePaneProps): ReactNode => {
  const model = usePickemGameTab({ poolId });
  const overlay = useOverlay();
  const { view, round, stage } = model;

  // ISSUE-1429. Once a contest is over, the Commissioner starts the next one
  // from this tab. Presentation only: the server refuses anyone else.
  const isCommissioner =
    viewerPoolRole === "owner" || viewerPoolRole === "admin";
  const startContestButton = (
    <Button
      fullWidth
      data-testid="pickem-start-contest"
      onClick={() => {
        overlay.open({ kind: "dialog", name: NEW_CONTEST_DIALOG_NAME });
      }}
    >
      {NEW_CONTEST_START_ACTION_LABEL}
    </Button>
  );

  let body: ReactNode;
  if (view.kind === "loading") {
    body = <LoadingCards testId="pickem-game-loading" />;
  } else if (view.kind === "failed") {
    body = (
      <div data-testid="pickem-game-failed">
        <InlineBanner
          variant="warning"
          message={PICKEM_GAME_FAILED}
          action={{ label: PICKEM_RETRY, onAction: model.retry }}
        />
      </div>
    );
  } else if (view.kind === "empty") {
    const canStart = canOfferPickemNewContest({
      emptyStateReason: view.reason,
      isCommissioner,
    });
    body = (
      <div className="flex flex-col gap-3">
        <p className="text-body text-ink" data-testid="pickem-game-empty">
          {PICKEM_EMPTY_STATE_NOTES[view.reason]}
        </p>
        {canStart ? startContestButton : null}
      </div>
    );
  } else {
    // An ended contest still shows its rounds, so the Commissioner's start
    // sits above them.
    const canStart = canOfferPickemNewContest({
      emptyStateReason:
        model.pool?.emptyState === "contestEnded"
          ? "contestEnded"
          : "noRoundsYet",
      isCommissioner,
    });
    body = (
      <>
        {canStart ? (
          <section
            className="flex flex-col gap-3"
            data-testid="pickem-next-contest"
          >
            <p className="text-body text-ink">{PICKEM_NEXT_CONTEST_LINE}</p>
            {startContestButton}
          </section>
        ) : null}
        <PickemRoundStrip
          rounds={model.pool?.rounds ?? []}
          selectedRoundId={model.selectedRoundId}
          onSelectRound={(roundId) => {
            void model.selectRound(roundId);
          }}
        />
        {renderRound()}
      </>
    );
  }

  function renderRound(): ReactNode {
    if (model.isRoundLoading) {
      return <LoadingCards testId="pickem-round-loading" />;
    }
    if (model.isRoundFailed || round === null || stage === null) {
      return (
        <div data-testid="pickem-round-failed">
          <InlineBanner
            variant="warning"
            message={PICKEM_GAME_FAILED}
            action={{ label: PICKEM_RETRY, onAction: model.retry }}
          />
        </div>
      );
    }
    const canPick = round.viewer.canPick;
    const cannotPick = round.viewer.cannotPickReason ?? null;
    const stillNeeded = Math.max(0, round.picksRequired - model.stagedCount);
    const nextLockAt = model.pool?.viewer.nextLockAt ?? null;
    const tiebreaker = round.tiebreaker;
    const tiebreakerGame =
      tiebreaker === null
        ? null
        : pickemGameLabel({
            teamsById: model.teamsById,
            awayTeamId: tiebreaker.awayTeamId,
            homeTeamId: tiebreaker.homeTeamId,
          });
    const showsConfidence =
      round.confidence !== null && (canPick || model.confidenceRows.length > 0);

    return (
      <>
        <section
          className="flex flex-col gap-1"
          data-testid="pickem-round-summary"
        >
          {round.myScore?.status === "scored" ? (
            <p
              className="text-body-strong text-ink"
              data-testid="pickem-my-score"
            >
              {pickemMyScoreLine(round.myScore)}
            </p>
          ) : null}
          {canPick ? (
            <>
              <p
                className="text-body-strong text-ink"
                data-testid="pickem-progress"
              >
                {pickemProgressLine({
                  made: model.stagedCount,
                  required: round.picksRequired,
                })}
              </p>
              <p className="text-body text-ink-secondary">
                {stillNeeded === 0
                  ? PICKEM_ALL_PICKS_IN
                  : pickemStillNeededLine(stillNeeded)}
              </p>
              {nextLockAt !== null ? (
                <p className="text-caption text-ink-secondary">
                  {pickemNextLockLine(
                    formatKickoffLabel({
                      isoInstant: nextLockAt,
                      timeZone: model.timeZone,
                    }),
                  )}
                </p>
              ) : null}
            </>
          ) : null}
          {cannotPick !== null ? (
            <p className="text-body text-ink" data-testid="pickem-cannot-pick">
              {PICKEM_CANNOT_PICK_NOTES[cannotPick]}
            </p>
          ) : null}
        </section>

        {model.isFullNoteShown ? (
          <div data-testid="pickem-full-note">
            <InlineBanner variant="neutral" message={PICKEM_PICKS_FULL_NOTE} />
          </div>
        ) : null}

        <section
          className="flex flex-col gap-3"
          aria-label={PICKEM_GAMES_HEADING}
        >
          {round.games.length === 0 ? (
            <p className="text-body text-ink-secondary">{PICKEM_NO_GAMES}</p>
          ) : (
            round.games.map((game) => (
              <PickemGameCard
                key={game.eventId}
                game={game}
                teamsById={model.teamsById}
                stagedPick={stage.picks[game.eventId] ?? null}
                isSpreadPool={round.rules.pickBasis === "spread"}
                canPick={canPick && !model.isSaving}
                nowMs={model.nowMs}
                timeZone={model.timeZone}
                onTapTeam={model.tapTeam}
              />
            ))
          )}
        </section>

        {showsConfidence ? (
          <PickemConfidenceOrder
            rows={model.confidenceRows}
            teamsById={model.teamsById}
            disabled={!canPick || model.isSaving}
            onMove={model.moveRank}
          />
        ) : null}

        {tiebreaker !== null && tiebreakerGame !== null ? (
          <PickemTiebreakerField
            tiebreaker={tiebreaker}
            gameLabel={tiebreakerGame}
            text={stage.tiebreakerText}
            isInvalid={model.isTiebreakerInvalid}
            disabled={!canPick || model.isSaving}
            onChangeText={model.setTiebreakerText}
          />
        ) : null}

        {canPick ? (
          <div className="flex flex-col gap-2">
            <Button
              fullWidth
              data-testid="pickem-save"
              disabled={!model.isDirty || model.isTiebreakerInvalid}
              loading={model.isSaving}
              loadingLabel={PICKEM_SAVING_LABEL}
              onClick={() => {
                void model.save();
              }}
            >
              {PICKEM_SAVE_LABEL}
            </Button>
            <SavedNote saved={model.isSaved} label={PICKEM_SAVED_LABEL} />
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="pickem-game-tab"
      data-pool-id={poolId}
    >
      {body}
      <SpreadConsentDialog
        open={model.pendingConsent !== null}
        teamName={model.pendingConsent?.teamName ?? ""}
        onConfirm={model.confirmConsent}
        onCancel={model.cancelConsent}
      />
    </div>
  );
};
